// Native replacements for the moment.js API surface previously used in this codebase.
// All functions are immutable: Dates passed in are never mutated.

export type DateInput = Date | string | number | null | undefined;
export type DateUnit =
  'millisecond' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';

const DATE_ONLY_PATTERN = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/;
const COMPACT_DATE_PATTERN = /^(\d{4})(\d{2})(\d{2})$/;
const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export function isValidDate(date: unknown): date is Date {
  return date instanceof Date && !isNaN(date.getTime());
}

// Like moment(value): calendar-date strings (YYYY, YYYY-MM, YYYY-MM-DD and the compact
// YYYYMMDD basic form) parse as local midnight rather than UTC, and out-of-range components
// (e.g. 2026-02-30) are invalid. Returns null for null/undefined/'', and an invalid Date for
// unparseable input.
export function parseDate(value: DateInput): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return new Date(value.getTime());
  if (typeof value === 'number') return new Date(value);

  const dateOnly = DATE_ONLY_PATTERN.exec(value) || COMPACT_DATE_PATTERN.exec(value);
  if (dateOnly) {
    return dateFromParts(Number(dateOnly[1]), Number(dateOnly[2] || 1), Number(dateOnly[3] || 1));
  }

  return new Date(value);
}

// Rejects component overflow (e.g. month 13 or Feb 30) the way moment does
function dateFromParts(year: number, month: number, day: number): Date {
  const date = new Date(year, month - 1, day);
  const inRange = date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return inRange ? date : new Date(NaN);
}

function normalizeUnit(unit: string): DateUnit {
  return (unit.endsWith('s') ? unit.slice(0, -1) : unit) as DateUnit;
}

// Maps 2-digit years the way moment does: 0-68 => 2000s, 69-99 => 1900s
function expandTwoDigitYear(year: number): number {
  return year + (year > 68 ? 1900 : 2000);
}

// Parses a string against explicit numeric formats built from M/MM, D/DD and YY/YYYY tokens
// (e.g. 'MM/DD/YYYY', 'M/D/YY'). Accepts a single format or a list, like moment(value, formats).
// When strict is false, separators and zero-padding are not enforced. Returns an invalid Date
// when nothing matches, mirroring moment's behavior.
export function parseWithFormats(value: string | null | undefined, formats: string | string[], strict = false): Date {
  const invalid = new Date(NaN);
  if (value == null || value === '') return invalid;

  for (const format of Array.isArray(formats) ? formats : [formats]) {
    const parsed = strict ? parseWithFormatStrict(value, format) : parseWithFormatLenient(value, format);
    if (parsed) return parsed;
  }

  return invalid;
}

function buildDate(parts: { year?: number, month?: number, day?: number }): Date | null {
  const { year, month = 1, day = 1 } = parts;
  if (year == null) return null;

  const date = dateFromParts(year, month, day);
  return isValidDate(date) ? date : null;
}

const FORMAT_TOKEN_PATTERN = /YYYY|YY|MM|M|DD|D/g;
const TOKEN_MAX_DIGITS: Record<string, number> = { YYYY: 4, YY: 2, MM: 2, M: 2, DD: 2, D: 2 };

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseWithFormatStrict(value: string, format: string): Date | null {
  const tokens: string[] = [];
  let pattern = '^';
  let lastIndex = 0;

  const tokenPattern = new RegExp(FORMAT_TOKEN_PATTERN.source, 'g');
  let tokenMatch;
  while ((tokenMatch = tokenPattern.exec(format)) !== null) {
    const token = tokenMatch[0];
    pattern += escapeRegExp(format.slice(lastIndex, tokenMatch.index));
    pattern += token === 'YYYY' ? '(\\d{4})' : (token === 'M' || token === 'D') ? '(\\d{1,2})' : '(\\d{2})';
    tokens.push(token);
    lastIndex = tokenMatch.index + token.length;
  }
  pattern += `${escapeRegExp(format.slice(lastIndex))}$`;

  const match = value.match(new RegExp(pattern));
  if (!match) return null;

  return buildDate(collectDateParts(tokens, match.slice(1)));
}

function parseWithFormatLenient(value: string, format: string): Date | null {
  const exact = parseWithFormatStrict(value, format);
  if (exact) return exact;

  const tokens = format.match(FORMAT_TOKEN_PATTERN) || [];
  const numbers = value.match(/\d+/g) || [];
  if (tokens.length === 0 || numbers.length !== tokens.length) return null;
  if (!/^[\d\s/.-]+$/.test(value)) return null;
  if (tokens.some((token, i) => numbers[i].length > TOKEN_MAX_DIGITS[token])) return null;

  return buildDate(collectDateParts(tokens, numbers));
}

function collectDateParts(tokens: string[], values: string[]): { year?: number, month?: number, day?: number } {
  const parts: { year?: number, month?: number, day?: number } = {};
  tokens.forEach((token, i) => {
    const number = parseInt(values[i], 10);
    if (token === 'YYYY') parts.year = number;
    if (token === 'YY') parts.year = expandTwoDigitYear(number);
    if (token === 'MM' || token === 'M') parts.month = number;
    if (token === 'DD' || token === 'D') parts.day = number;
  });
  return parts;
}

export function daysInMonth(date: Date): number {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

// Like moment's add: months/years clamp the day-of-month (Jan 31 + 1 month = Feb 28),
// days/weeks preserve the local clock time across DST, hours and below are exact.
// Nullish input yields an invalid Date, mirroring moment's inert invalid-moment chaining.
export function add(date: Date | null | undefined, amount: number | string, unit: string): Date {
  if (date == null) return new Date(NaN);

  const value = Number(amount);
  const result = new Date(date.getTime());

  switch (normalizeUnit(unit)) {
  case 'year':
    return addMonths(result, value * 12);
  case 'quarter':
    return addMonths(result, value * 3);
  case 'month':
    return addMonths(result, value);
  case 'week':
    result.setDate(result.getDate() + value * 7);
    return result;
  case 'day':
    result.setDate(result.getDate() + value);
    return result;
  case 'hour':
    return new Date(result.getTime() + value * MS_PER_HOUR);
  case 'minute':
    return new Date(result.getTime() + value * MS_PER_MINUTE);
  case 'second':
    return new Date(result.getTime() + value * MS_PER_SECOND);
  case 'millisecond':
    return new Date(result.getTime() + value);
  default:
    return result;
  }
}

export function subtract(date: Date | null | undefined, amount: number | string, unit: string): Date {
  return add(date, -Number(amount), unit);
}

function addMonths(date: Date, months: number): Date {
  const dayOfMonth = date.getDate();
  const result = new Date(date.getTime());
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  result.setDate(Math.min(dayOfMonth, daysInMonth(result)));
  return result;
}

export function startOf(date: Date, unit: string): Date {
  const result = new Date(date.getTime());

  switch (normalizeUnit(unit)) {
  case 'year':
    result.setMonth(0, 1);
    result.setHours(0, 0, 0, 0);
    return result;
  case 'quarter':
    result.setMonth(Math.floor(result.getMonth() / 3) * 3, 1);
    result.setHours(0, 0, 0, 0);
    return result;
  case 'month':
    result.setDate(1);
    result.setHours(0, 0, 0, 0);
    return result;
  case 'week':
    result.setDate(result.getDate() - result.getDay());
    result.setHours(0, 0, 0, 0);
    return result;
  case 'day':
    result.setHours(0, 0, 0, 0);
    return result;
  case 'hour':
    result.setMinutes(0, 0, 0);
    return result;
  case 'minute':
    result.setSeconds(0, 0);
    return result;
  case 'second':
    result.setMilliseconds(0);
    return result;
  default:
    return result;
  }
}

export function endOf(date: Date, unit: string): Date {
  const normalized = normalizeUnit(unit);
  if (normalized === 'millisecond') return new Date(date.getTime());

  return new Date(add(startOf(date, normalized), 1, normalized).getTime() - 1);
}

export function isSameDay(a: DateInput, b: DateInput): boolean {
  const dateA = parseDate(a);
  const dateB = parseDate(b);
  if (!isValidDate(dateA) || !isValidDate(dateB)) return false;

  return startOf(dateA, 'day').getTime() === startOf(dateB, 'day').getTime();
}

export function isBeforeDay(a: Date, b: Date): boolean {
  return startOf(a, 'day') < startOf(b, 'day');
}

export function isAfterDay(a: Date, b: Date): boolean {
  return startOf(a, 'day') > startOf(b, 'day');
}

// moment truncates diffs toward zero
function truncate(value: number): number {
  return value < 0 ? Math.ceil(value) : Math.floor(value);
}

export function diffHours(a: Date, b: Date): number {
  return truncate((a.getTime() - b.getTime()) / MS_PER_HOUR);
}

// Calendar-day distance; rounding absorbs DST-shortened/lengthened days
export function diffDays(a: Date, b: Date): number {
  return Math.round((startOf(a, 'day').getTime() - startOf(b, 'day').getTime()) / MS_PER_DAY);
}

export function diffMonths(a: Date, b: Date): number {
  return truncate(diffMonthsExact(a, b));
}

// Replicates moment's fractional month diff exactly, including its day-of-month
// asymmetry guard and month-length-based fractional adjustment
export function diffMonthsExact(a: Date, b: Date): number {
  if (a.getDate() < b.getDate()) return -diffMonthsExact(b, a);

  const wholeMonths = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  const anchor = addMonths(a, wholeMonths);

  let adjustment;
  if (b.getTime() - anchor.getTime() < 0) {
    const previousAnchor = addMonths(a, wholeMonths - 1);
    adjustment = (b.getTime() - anchor.getTime()) / (anchor.getTime() - previousAnchor.getTime());
  } else {
    const nextAnchor = addMonths(a, wholeMonths + 1);
    adjustment = (b.getTime() - anchor.getTime()) / (nextAnchor.getTime() - anchor.getTime());
  }

  return -(wholeMonths + adjustment) || 0;
}

// moment converts a millisecond duration to months using the 400-year average month length
export function millisecondsToMonths(milliseconds: number): number {
  return (milliseconds / MS_PER_DAY) * 4800 / 146097;
}

export function guessTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Short local time zone name, e.g. "CDT" (moment-timezone's `z` token)
export function timeZoneAbbreviation(date: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' }).formatToParts(date);
  return parts.find(part => part.type === 'timeZoneName')?.value || '';
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function twelveHour(date: Date): number {
  return date.getHours() % 12 || 12;
}

const TOKEN_PATTERN = /\[([^\]]*)]|YYYY|YY|MMMM|MMM|MM|M|dddd|ddd|DD|Do|D|Q|HH|H|hh|h|mm|m|SSS|ss|s|A|a|z/g;

// Formats a date with the subset of moment tokens used in this codebase.
// Square brackets escape literal text; unrecognized characters pass through.
export function formatTokens(date: DateInput, format: string): string {
  const parsed = parseDate(date);
  if (!isValidDate(parsed)) return '';

  return format.replace(TOKEN_PATTERN, (token, literal) => {
    if (literal !== undefined) return literal;

    switch (token) {
    case 'YYYY': return String(parsed.getFullYear());
    case 'YY': return pad(parsed.getFullYear() % 100);
    case 'MMMM': return MONTH_NAMES[parsed.getMonth()];
    case 'MMM': return MONTH_NAMES[parsed.getMonth()].slice(0, 3);
    case 'MM': return pad(parsed.getMonth() + 1);
    case 'M': return String(parsed.getMonth() + 1);
    case 'dddd': return DAY_NAMES[parsed.getDay()];
    case 'ddd': return DAY_NAMES[parsed.getDay()].slice(0, 3);
    case 'DD': return pad(parsed.getDate());
    case 'Do': return ordinal(parsed.getDate());
    case 'D': return String(parsed.getDate());
    case 'Q': return String(Math.floor(parsed.getMonth() / 3) + 1);
    case 'HH': return pad(parsed.getHours());
    case 'H': return String(parsed.getHours());
    case 'hh': return pad(twelveHour(parsed));
    case 'h': return String(twelveHour(parsed));
    case 'mm': return pad(parsed.getMinutes());
    case 'm': return String(parsed.getMinutes());
    case 'SSS': return pad(parsed.getMilliseconds(), 3);
    case 'ss': return pad(parsed.getSeconds());
    case 's': return String(parsed.getSeconds());
    case 'A': return parsed.getHours() < 12 ? 'AM' : 'PM';
    case 'a': return parsed.getHours() < 12 ? 'am' : 'pm';
    case 'z': return timeZoneAbbreviation(parsed);
    default: return token;
    }
  });
}

// ISO 8601 with the local UTC offset, like moment's default .format()
// (e.g. "2026-07-24T15:04:05-05:00"). Nullish or invalid input returns null.
export function toISOStringWithOffset(date: Date | null | undefined): string | null {
  if (!isValidDate(date)) return null;

  const offsetMinutes = -date.getTimezoneOffset();
  const sign = offsetMinutes < 0 ? '-' : '+';
  const absOffset = Math.abs(offsetMinutes);
  const offset = `${sign}${pad(Math.floor(absOffset / 60))}:${pad(absOffset % 60)}`;

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${offset}`;
}

export interface CalendarFormats {
  sameDay: string;
  nextDay: string;
  nextWeek: string;
  lastDay: string;
  lastWeek: string;
  sameElse: string;
}

// Replicates moment's .calendar(null, formats) bucketing by calendar-day distance from today
export function calendarFormat(date: DateInput, formats: CalendarFormats, now: Date = new Date()): string {
  const parsed = parseDate(date);
  if (!isValidDate(parsed)) return '';

  const dayDiff = diffDays(parsed, now);

  let format;
  if (dayDiff < -6) {
    format = formats.sameElse;
  } else if (dayDiff < -1) {
    format = formats.lastWeek;
  } else if (dayDiff < 0) {
    format = formats.lastDay;
  } else if (dayDiff < 1) {
    format = formats.sameDay;
  } else if (dayDiff < 2) {
    format = formats.nextDay;
  } else if (dayDiff < 7) {
    format = formats.nextWeek;
  } else {
    format = formats.sameElse;
  }

  return formatTokens(parsed, format);
}

// English ordinal (1st, 2nd, 3rd, ...), like moment.localeData().ordinal(n)
export function ordinal(number: number): string {
  const mod100 = number % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${number}th`;

  switch (number % 10) {
  case 1: return `${number}st`;
  case 2: return `${number}nd`;
  case 3: return `${number}rd`;
  default: return `${number}th`;
  }
}

// Matches moment-duration-format's default .format() output for sub-day durations,
// as used by the call timer: 0 => "0 seconds", 65s => "1:05", 3661s => "1:01:01"
export function formatDurationClock(totalSeconds: number): string {
  if (!totalSeconds) return '0 seconds';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  return `${minutes}:${pad(seconds)}`;
}
